import { ROTATE_HANDLE_CENTER_OFFSET } from '@/lib/editor/constants/rotate-handle-center-offset'
import {
  MOVE_BADGE_DEFAULT_SCREEN_SIZE,
  ROTATE_BADGE_CORNER_CLEARANCE_PX,
  ROTATE_BADGE_SIZE_RATIO,
  ROTATE_BASE_ANGLE_DEG,
  ROTATE_LINE_LENGTH_RATIO,
} from '@/lib/editor/constants/crop-overlay-constants'

const MOVE_BADGE_MIN_SCREEN_SIZE = 20
/** Badge never claims more than this fraction of the shape's smaller on-screen dimension. */
const MOVE_BADGE_MAX_SHAPE_FRACTION = 0.6
/** Icon is drawn at this fraction of the badge's own diameter (16px icon inside a 40px badge). */
const MOVE_BADGE_ICON_RATIO = 16 / 40

/** Icon is drawn at this fraction of the rotate badge's own diameter (16px icon inside 24px). */
const ROTATE_BADGE_ICON_RATIO = 16 / 24
const ROTATE_STEM_DOT_SCREEN_RADIUS = 2

/** The shape's VISUAL rect in doc space — what the frame, dots and badges are drawn around. */
export interface CropVisualRect {
  x: number
  y: number
  width: number
  height: number
}

export interface MoveBadgeLayout {
  centerX: number
  centerY: number
  /** On-screen badge diameter (px) — the rotate dial is sized off it. */
  badgeScreenSize: number
  badgeR: number
  iconTransform: string
}

/**
 * Center move badge — a purely decorative affordance (dragging to pan works from anywhere in
 * the interior rect, not just the badge itself). Sized in on-screen px terms — shrinks
 * proportionally once the shape is smaller than the default badge size, floored so it never
 * disappears — then converted to doc-space (÷ scale) so the ambient canvas-zoom transform
 * renders it at exactly that screen size, the same technique the resize handles use.
 */
export function computeMoveBadgeLayout(visual: CropVisualRect, scale: number): MoveBadgeLayout {
  const { x: vx, y: vy, width: vw, height: vh } = visual
  const centerX = vx + vw / 2
  const centerY = vy + vh / 2
  const shapeScreenMinDim = Math.min(vw, vh) * scale
  const badgeScreenSize = Math.max(
    MOVE_BADGE_MIN_SCREEN_SIZE,
    Math.min(MOVE_BADGE_DEFAULT_SCREEN_SIZE, shapeScreenMinDim * MOVE_BADGE_MAX_SHAPE_FRACTION)
  )
  const badgeDocSize = badgeScreenSize / scale
  const badgeR = badgeDocSize / 2
  const iconDocSize = badgeDocSize * MOVE_BADGE_ICON_RATIO
  const iconScale = iconDocSize / 16
  const iconTransform = `translate(${centerX - iconDocSize / 2}, ${centerY - iconDocSize / 2}) scale(${iconScale})`

  return { centerX, centerY, badgeScreenSize, badgeR, iconTransform }
}

export interface RotateDialLayout {
  stemStartX: number
  stemStartY: number
  stemDotR: number
  lineEndX: number
  lineEndY: number
  rotateCenterX: number
  rotateCenterY: number
  rotateR: number
  rotateIconTransform: string
}

/**
 * Rotate handle — a badge connected to the move badge by a line that acts like a dial pointer
 * for the PHOTO's own rotation (`photoFill.rotation`), independent of the shape/path's own
 * `rotation`: at photo rotation 0 it points up-and-right (ROTATE_BASE_ANGLE_DEG); as the photo
 * rotates, the same local direction is carried along so the line always ends up pointing at
 * "up-and-right relative to the photo's current rotation". The caller passes the live ref's
 * rotation (not the shape prop's) so the dial follows the in-flight value during a drag, before
 * the prop round-trips. The rotate icon GLYPH itself stays upright (no rotation applied to it) —
 * only its position moves.
 */
export function computeRotateDialLayout(
  visual: CropVisualRect,
  moveBadge: MoveBadgeLayout,
  photoRotation: number,
  scale: number,
  sw: number,
  handleHalf: number
): RotateDialLayout {
  const { width: vw, height: vh } = visual
  const { centerX, centerY, badgeScreenSize, badgeR } = moveBadge
  const rotRad = (photoRotation * Math.PI) / 180
  const cosR = Math.cos(rotRad)
  const sinR = Math.sin(rotRad)
  const baseAngleRad = (ROTATE_BASE_ANGLE_DEG * Math.PI) / 180
  const localDirX = Math.cos(baseAngleRad)
  const localDirY = Math.sin(baseAngleRad)
  const dirX = localDirX * cosR - localDirY * sinR
  const dirY = localDirX * sinR + localDirY * cosR

  const rotateBadgeScreenSize = badgeScreenSize * ROTATE_BADGE_SIZE_RATIO
  const rotateBadgeDocSize = rotateBadgeScreenSize / scale
  const rotateR = rotateBadgeDocSize / 2
  const rotateIconDocSize = rotateBadgeDocSize * ROTATE_BADGE_ICON_RATIO
  const rotateIconScale = rotateIconDocSize / 16
  const lineLengthDoc = (badgeScreenSize * ROTATE_LINE_LENGTH_RATIO) / scale
  const stemDotR = ROTATE_STEM_DOT_SCREEN_RADIUS / scale

  // Start the stem exactly at the OUTER edge of the move badge's stroke (badgeR is the circle's
  // bare radius, i.e. the stroke's centerline — starting there makes the line visually protrude
  // half a stroke-width into the badge's transparent interior instead of meeting its edge).
  const moveBadgeOuterR = badgeR + sw / 2
  const stemStartX = centerX + dirX * moveBadgeOuterR
  const stemStartY = centerY + dirY * moveBadgeOuterR
  const defaultLineEndDist = badgeR + lineLengthDoc
  const defaultRotateCenterDist = defaultLineEndDist + rotateR

  // `badgeScreenSize` shrinks proportionally with the shape once it's smaller than the default
  // badge size, but only down to `MOVE_BADGE_MIN_SCREEN_SIZE` — once the shape shrinks past the
  // point where the badge hits that floor, the badge (and this dial's reach) stays a FIXED
  // screen size while the resize handles at the visual rect's own corners keep shrinking with
  // it. For a "just right" mid-size shape this can land the dial right on top of a corner
  // handle; push it out to just clear the corner in that case ONLY — a corner much closer to
  // center than the dial's default reach (small shape) is already safely inside it, and a
  // corner much farther out (large shape) already sits well beyond the dial, so extending the
  // dial's reach unconditionally toward "always clear the corner" would instead push it
  // absurdly far outside a large shape entirely. Uses the CORNER (diagonal) distance rather
  // than the direction the dial happens to be facing so its reach stays constant as it's
  // dragged around a full circle, instead of growing/shrinking as it passes corners vs. flat
  // edges.
  const cornerReachDoc = Math.sqrt((vw / 2) ** 2 + (vh / 2) ** 2)
  const cornerClearanceDoc = handleHalf + ROTATE_BADGE_CORNER_CLEARANCE_PX / scale + rotateR
  const wouldOverlapCorner = Math.abs(defaultRotateCenterDist - cornerReachDoc) < cornerClearanceDoc
  const rotateCenterDist = wouldOverlapCorner
    ? cornerReachDoc + cornerClearanceDoc
    : defaultRotateCenterDist
  const lineEndDist = rotateCenterDist - rotateR

  const lineEndX = centerX + dirX * lineEndDist
  const lineEndY = centerY + dirY * lineEndDist
  const rotateCenterX = centerX + dirX * rotateCenterDist
  const rotateCenterY = centerY + dirY * rotateCenterDist
  const rotateIconTransform = `translate(${rotateCenterX - rotateIconDocSize / 2}, ${rotateCenterY - rotateIconDocSize / 2}) scale(${rotateIconScale})`

  return {
    stemStartX,
    stemStartY,
    stemDotR,
    lineEndX,
    lineEndY,
    rotateCenterX,
    rotateCenterY,
    rotateR,
    rotateIconTransform,
  }
}

export interface ShapeRotateDotLayout {
  stemStartX: number
  stemStartY: number
  dotX: number
  dotY: number
}

/**
 * Shape rotate handle — the "outer select area" rotate handle normal (non-crop) selection
 * chrome shows above every shape, positioned and sized the same way (stem above the visual
 * rect's top-center, ROTATE_HANDLE_CENTER_OFFSET screen px converted to doc space). Rendered
 * inside the rotation group so it tracks the shape's current orientation automatically, same
 * as the resize handles.
 */
export function computeShapeRotateDotLayout(
  visual: CropVisualRect,
  scale: number,
  sw: number,
  handleHalf: number
): ShapeRotateDotLayout {
  const { x: vx, y: vy, width: vw } = visual
  const shapeRotateStemLength = ROTATE_HANDLE_CENTER_OFFSET / scale
  const shapeRotateHandleX = vx + vw / 2
  const shapeRotateHandleY = vy - shapeRotateStemLength
  // Start the stem at the OUTER edge of the top-middle resize handle's stroke, not its bare
  // center (handleHalf is the circle's radius to its stroke centerline) — starting at the center
  // makes the line visually cut through the whole node instead of meeting its edge, same issue
  // fixed earlier for the move-badge/rotate-photo-badge stem.
  const nHandleOuterR = handleHalf + sw / 2

  return {
    stemStartX: vx + vw / 2,
    stemStartY: vy - nHandleOuterR,
    dotX: shapeRotateHandleX,
    dotY: shapeRotateHandleY,
  }
}
